/* ========================================================================
   Dashboard
 ========================================================================== */

app.dashboard = (function () {
  'use strict';

  let _wrapper = null;
  let _jsonData = null;
  let _loader = null;

  let _pie = null;
  let _svg = null;
  let _chartGroup = null;
  let _arc = null;

  let _currentWidth = 0;
  let _currentHeight = 0;
  let _currentRadius = 0;
  let _currentYear = 2020;
  let _sliceCount = 95;

  const _colors = new Map([
    ['minder', '#ff6691'],
    ['meer', '#392d64'],
    ['anders', '#01c5dd'],
    ['nieuw', '#ffffff'],
  ]);

  function init() {
    _wrapper = document.querySelector('[data-dashboard]');
    _loader = LoadingOverlay('body');

    fetchDashboardData();
    initYearButtons();

    app.dashboardHelpButtons.init();
    app.dashboardMetricsPanel.init();
    app.dashboardActivitySlideout.init();
    app.dashboardVideoLightbox.init();

    // Handle pie chart size on resize
    window.addEventListener(
      'resize',
      () => {
        updatePieChartSize();
      },
      { passive: true }
    );
  }

  //
  // Init year buttons
  //
  function initYearButtons() {
    const yearButtons = document.querySelectorAll('[data-dashboard-year]');

    yearButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        _currentYear = parseInt(e.currentTarget.dataset.dashboardYear);
        updatePieChart(_currentYear);
        setActiveYearButton();

        // DATALAYER
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dashboardYearClick',
          option: _currentYear.toString(),
        });
      });
    });
  }

  //
  // Fetch dashboard data from API
  //
  function fetchDashboardData() {
    fetch('/api/dashboard/getdata', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        // Check if the response is okay
        if (response.ok === false) throw new Error('Network response was not ok');

        return response.text();
      })
      .then((data) => {
        _jsonData = JSON.parse(data);
        initPieChart();
      })
      .catch((error) => {
        console.error(error);
        Toast(error, 'error');
        _loader.close();
      });
  }

  //
  // Init pie chart
  //
  function initPieChart() {
    // Select the container and get its dimensions
    _pie = d3.select('[data-dashboard-pie]');

    _currentWidth = _pie.node().getBoundingClientRect().width;
    _currentHeight = _pie.node().getBoundingClientRect().height;

    // Define radius based on the container size
    _currentRadius = Math.min(_currentWidth, _currentHeight) / 2;

    // Create an SVG container
    _svg = _pie.select('svg').attr('width', _currentWidth).attr('height', _currentHeight);

    // Create the chart group
    _chartGroup = _svg.append('g').attr('transform', `translate(${_currentWidth / 2}, ${_currentHeight / 2})`);

    // Create an arc generator
    _arc = d3.arc().innerRadius(0).outerRadius(_currentRadius);

    // Get current year from query string
    if (util.getQueryString('year') !== '') {
      const selectedYear = parseInt(util.getQueryString('year'));
      if (isNaN(selectedYear) === false) _currentYear = selectedYear;
    }

    const data = generateData();
    const pieData = d3.pie().value((d) => d.value)(data);
    const slices = _chartGroup
      .selectAll('path')
      .data(pieData)
      .enter()
      .append('path')
      .attr('class', 'slice')
      .attr('d', _arc)
      .attr('fill', (d) => d.data.color);

    drawLines(slices);
    bindActivityData();
    bindMetricsData();
    setActiveYearButton();
    setYearSlider();

    _wrapper.classList.remove('is-loading');
    _loader.close();
  }

  //
  // Update pie chart
  //
  function updatePieChart() {
    // Recalculate the pie data
    const data = generateData();
    const pieData = d3.pie().value((d) => d.value)(data);
    const slices = _chartGroup.selectAll('path').data(pieData);

    slices
      .enter()
      .append('path')
      .attr('class', 'slice')
      .merge(slices)
      .transition()
      .duration(500)
      .attr('fill', (d) => d.data.color);

    // _chartGroup.selectAll('path')._groups[0].forEach((slice, i) => {
    //   setTimeout(() => {
    //     slice.style.fill = data[i].color;
    //   }, i * 10);
    // });

    bindActivityData();
    bindMetricsData();
  }

  //
  // Update pie chart size
  //
  const updatePieChartSize = util.debounce(() => {
    _currentWidth = _pie.node().getBoundingClientRect().width;
    _currentHeight = _pie.node().getBoundingClientRect().height;
    _currentRadius = Math.min(_currentWidth, _currentHeight) / 2;

    // Update SVG dimensions
    _svg.attr('width', _currentWidth).attr('height', _currentHeight);

    // Update chart group transform
    _chartGroup.attr('transform', `translate(${_currentWidth / 2}, ${_currentHeight / 2})`);

    // Update the arc generator
    _arc.innerRadius(0).outerRadius(_currentRadius);

    // Redraw the pie slices
    const slices = _chartGroup.selectAll('path').attr('d', _arc);

    // Redraw the lines
    drawLines(slices);
  }, 250);

  //
  // Draw divider lines
  //
  function drawLines(slices) {
    // Remove old lines
    _chartGroup.selectAll('.divider').remove();

    // Center of the pie chart
    const centerX = 0;
    const centerY = 0;

    // Draw dividing lines between slices
    slices.each(function (d, i) {
      // Calculate end point on the outer edge of the pie slice
      const outerX = _currentRadius * Math.cos(d.startAngle - Math.PI / 2);
      const outerY = _currentRadius * Math.sin(d.startAngle - Math.PI / 2);

      // Draw the line
      _chartGroup
        .append('line')
        .attr('class', 'divider')
        .attr('x1', centerX)
        .attr('y1', centerY)
        .attr('x2', outerX)
        .attr('y2', outerY)
        .style('stroke', '#685b8f') // Set line color
        .style('stroke-width', 1); // Set line width
    });
  }

  //
  // Generate pie chart data for current year
  //
  // function generateDataSimple() {
  //   let data = [];

  //   const yearData = jsonPath(_jsonData, `$.years[?(@.year == ${_currentYear})]`)[0];

  //   if (typeof yearData !== 'object') {
  //     Toast(`Geen data gevonden voor ${_currentYear}.`, 'error');
  //     return data;
  //   }

  //   const moreCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "meer")].total'));
  //   const diffCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "anders")].total'));
  //   const newCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "nieuw")].total'));
  //   const lessCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "minder")].total'));

  //   // Fill the data array
  //   for (let i = 0; i < moreCount; i++) {
  //     data.push({ value: 1, color: _colors.get('meer') });
  //   }
  //   for (let i = 0; i < diffCount; i++) {
  //     data.push({ value: 1, color: _colors.get('anders') });
  //   }
  //   for (let i = 0; i < newCount; i++) {
  //     data.push({ value: 1, color: _colors.get('nieuw') });
  //   }
  //   for (let i = 0; i < lessCount; i++) {
  //     data.push({ value: 1, color: _colors.get('minder') });
  //   }

  //   return data;
  // }

  //
  // Generate pie chart data for current year
  //
  function generateData() {
    let data = [];
    let tempArr = generateTempArray();

    tempArr.forEach((item) => {
      data.push({ value: 1, color: _colors.get(item) });
    });

    return data;
  }

  //
  // Generate temporary array with 95 items
  //
  function generateTempArray() {
    let tempArr = [];

    // Init array with 95 'minder' items
    for (let i = 0; i < _sliceCount; i++) {
      tempArr.push('minder');
    }

    const yearData = jsonPath(_jsonData, `$.years[?(@.year == ${_currentYear})]`)[0];

    if (typeof yearData !== 'object') {
      Toast(`Geen data gevonden voor ${_currentYear}.`, 'error');
      return data;
    }

    const meerCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "meer")].total'));
    const andersCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "anders")].total'));
    const nieuwCount = sumArray(jsonPath(yearData, '$.activities[?(@.goal == "nieuw")].total'));

    pushToTempArray(tempArr, 'meer', 12, meerCount);
    pushToTempArray(tempArr, 'anders', 36, andersCount);
    pushToTempArray(tempArr, 'nieuw', 60, nieuwCount);

    return tempArr;
  }

  //
  // Push value to temporary array, from center outwards
  //
  function pushToTempArray(tempArr, value, startIndex, count) {
    if (count === 0) return;

    // Make sure the start index is on an available slot, otherwise increase the index
    while (tempArr[startIndex] !== 'minder') {
      startIndex++;
    }

    // Set the start index value
    tempArr[startIndex] = value;

    let pushedItems = 1;
    let step = 1;
    let currentIndex = startIndex;

    // Use real index, because the array is circular
    let realIndex;

    // Loop through the array and push the value to the next available slot
    while (pushedItems < count) {
      currentIndex += step;
      realIndex = getRealIndex(currentIndex, _sliceCount);

      // If there's no room left before the starting index, only add slices after the starting index
      if (currentIndex < startIndex && tempArr[realIndex] !== 'minder') {
        step = getNextStep(step);
        continue;
      }

      // Set the value
      tempArr[realIndex] = value;

      // Change step to make it work center outwards
      step = getNextStep(step);

      // Increase counter
      pushedItems++;
    }
  }

  //
  // Get next step
  //
  function getNextStep(step) {
    return (Math.abs(step) + 1) * (step > 0 ? -1 : 1);
  }

  //
  // Get next index, array is circular
  //
  function getRealIndex(index, maxlength) {
    if (index >= maxlength) {
      return index % maxlength;
    }
    if (index >= 0) return index;
    return index % maxlength === 0 ? 0 : maxlength + (index % maxlength);
  }

  //
  // Sum numbers in array
  //
  function sumArray(array) {
    return array.reduce((a, b) => a + b, 0);
  }

  //
  // Set active year button
  //
  function setActiveYearButton() {
    const yearButtons = document.querySelectorAll('[data-dashboard-year]');

    yearButtons.forEach((button) => {
      if (button.dataset.dashboardYear.toString() === _currentYear.toString()) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });

    setYearHelpButtons();
  }

  //
  // Show/hide year help buttons
  //
  function setYearHelpButtons() {
    // Show help buttons for current year
    const helpButtons = document.querySelectorAll('[data-help-year]');
    helpButtons.forEach((button) => {
      if (button.dataset.helpYear.toString() === _currentYear.toString()) {
        button.style.display = 'block';
      } else {
        button.style.display = 'none';
      }
    });
  }

  //
  // Set year slider
  //
  function setYearSlider() {
    const slider = document.querySelector('[data-dashboard-year-slider]');
    if (!slider) return;

    const activeLink = slider.querySelector('a.is-active');
    if (!activeLink) return;

    const sliderRect = slider.getBoundingClientRect();
    const activeLinkRect = activeLink.getBoundingClientRect();

    if (activeLinkRect.right > sliderRect.right) {
      slider.scrollLeft = activeLinkRect.right - sliderRect.right + 32;
    }
  }

  //
  // Bind activity data
  //
  function bindActivityData() {
    const activityEls = document.querySelectorAll('[data-activity-id]');
    const yearData = jsonPath(_jsonData, `$.years[?(@.year == ${_currentYear})]`)[0];

    activityEls.forEach((activityEl) => {
      const activityId = parseInt(activityEl.dataset.activityId.toString());
      const progressEl = activityEl.querySelector('[data-activity-progress]');
      const progressBar = activityEl.querySelector('[data-activity-progress-bar]');
      const activityData = jsonPath(yearData, `$.activities[?(@.id == ${activityId})]`)[0];

      const barWidth = 22 + (activityData.total / 5) * 10;

      progressEl.dataset.activityProgress = activityData.total;
      progressBar.style.width = `${barWidth}px`;
      progressBar.innerHTML = `${activityData.total}%`;
    });
  }

  //
  // Bind metrics data
  //
  function bindMetricsData() {
    const yearData = jsonPath(_jsonData, `$.years[?(@.year == ${_currentYear})]`)[0];
    const metricsV = document.querySelector('[data-metrics-v]');
    const metricsB = document.querySelector('[data-metrics-b]');
    const metricsO = document.querySelector('[data-metrics-o]');
    const metricsVText = document.querySelector('[data-metrics-v-text]');
    const metricsBText = document.querySelector('[data-metrics-b-text]');
    const metricsOText = document.querySelector('[data-metrics-o-text]');
    const clientsText = document.querySelector('[data-metrics-clients-text]');
    const clientsImgs = document.querySelectorAll('[data-metrics-clients-img]');
    const socialBar = document.querySelector('[data-metrics-social]');
    const socialText = document.querySelector('[data-metrics-social-text]');
    const lovedOnesBar = document.querySelector('[data-metrics-lovedones]');
    const lovedOnesText = document.querySelector('[data-metrics-lovedones-text]');

    metricsV.style.width = `${yearData.totalV}%`;
    metricsB.style.width = `${yearData.totalB}%`;
    metricsO.style.width = `${yearData.totalO}%`;
    metricsVText.innerHTML = `${yearData.totalV}%`;
    metricsBText.innerHTML = `${yearData.totalB}%`;
    metricsOText.innerHTML = `${yearData.totalO}%`;

    let clientTextString = (yearData.clients - 100).toString();
    let extraClients = yearData.clients - 100;
    if (clientTextString.length === 1) clientTextString = '0' + clientTextString;

    clientsText.dataset.metricsClientsText = yearData.clients - 100;
    clientsText.innerHTML = `${clientTextString}`;

    clientsImgs.forEach((img) => {
      if (extraClients >= 10) {
        img.src = '/icons/man-blue.svg';
        extraClients -= 10;
      } else if (extraClients >= 5) {
        img.src = '/icons/man-half.svg';
        extraClients -= 10;
      } else {
        img.src = '/icons/man-white.svg';
      }
    });

    socialBar.style.width = `${yearData.socialDomain}%`;
    socialText.innerHTML = `${yearData.socialDomain}%`;

    lovedOnesBar.style.width = `${yearData.lovedOnes}%`;
    lovedOnesText.innerHTML = `${yearData.lovedOnes}%`;
  }

  // Expose public functions and variables
  return {
    init: init,
  };
})();

//
// Dashboard help buttons
//
app.dashboardHelpButtons = (function () {
  let _wrapper = null;

  let _openHelpItem = {
    helpItem: null,
    helpButton: null,
    helpText: null,
  };

  function init() {
    const helpItems = document.querySelectorAll('[data-help]');
    if (helpItems.length === 0) return;

    _wrapper = document.querySelector('[data-main-wrapper]');

    helpItems.forEach((helpItem) => {
      const helpButton = helpItem.querySelector('[data-help-button]');
      const helpText = helpItem.querySelector('[data-help-text]');
      const helpCloseButton = helpItem.querySelector('[data-help-close]');

      helpButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (helpButton.getAttribute('aria-expanded') !== 'true') {
          openHelp(helpButton, helpItem, helpText);
        } else {
          closeHelp();
        }
      });

      helpCloseButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeHelp();
      });
    });
  }

  function openHelp(helpButton, helpItem, helpText) {
    // Close other help items
    closeHelp();

    // Calculate position
    calculatePosition(helpText);

    // Show help text
    helpButton.setAttribute('aria-expanded', 'true');
    helpItem.classList.add('is-open');
    helpText.style.visibility = 'visible';
    helpText.classList.add('is-open');

    _openHelpItem.helpItem = helpItem;
    _openHelpItem.helpButton = helpButton;
    _openHelpItem.helpText = helpText;
  }

  function closeHelp() {
    if (_openHelpItem.helpItem === null) return;

    let helpButton = _openHelpItem.helpButton;
    let helpItem = _openHelpItem.helpItem;
    let helpText = _openHelpItem.helpText;

    helpButton.setAttribute('aria-expanded', 'false');
    helpItem.classList.remove('is-open');
    helpText.classList.remove('is-open');

    window.setTimeout(() => {
      helpText.style.visibility = 'hidden';
    }, 500);

    // Reset open help item
    _openHelpItem = {
      helpItem: null,
      helpButton: null,
      helpText: null,
    };
  }

  function calculatePosition(elem) {
    let positionY = 'top';
    let positionX = 'center';

    // Reset position
    elem.dataset.position = 'top center';

    const elemRect = elem.getBoundingClientRect();
    const wrapperRect = _wrapper.getBoundingClientRect();

    const isOutsideTop = elemRect.top < wrapperRect.top;
    const isOutsideBottom = elemRect.bottom > wrapperRect.bottom;
    const isOutsideLeft = elemRect.left < wrapperRect.left;
    const isOutsideRight = elemRect.right > wrapperRect.right;

    if (isOutsideTop) {
      positionY = 'bottom';
    } else if (isOutsideBottom) {
      positionY = 'top';
    }

    if (isOutsideRight) {
      positionX = 'left';
    } else if (isOutsideLeft) {
      positionX = 'right';
    } else {
      positionX = 'center';
    }

    elem.dataset.position = `${positionY} ${positionX}`;
  }

  // Expose public functions and variables
  return {
    init: init,
    closeHelp: closeHelp,
  };
})();

//
// Dashboard metrics panel
//
app.dashboardMetricsPanel = (function () {
  let _panel = null;
  let _toggle = null;
  let _close = null;

  function init() {
    _panel = document.querySelector('[data-metrics-panel]');
    _toggle = document.querySelector('[data-metrics-toggle]');
    _close = document.querySelector('[data-metrics-close]');

    _toggle.addEventListener('click', (e) => {
      e.preventDefault();
      openPanel();
    });

    _close.addEventListener('click', (e) => {
      e.preventDefault();
      closePanel();
      app.dashboardHelpButtons.closeHelp();
    });
  }

  function openPanel() {
    _panel.classList.add('is-open');
    _panel.style.visibility = 'visible';
    _toggle.setAttribute('aria-expanded', 'true');
  }

  function closePanel() {
    _panel.classList.remove('is-open');

    window.setTimeout(() => {
      _panel.style.visibility = 'hidden';
      _toggle.setAttribute('aria-expanded', 'false');
    }, 500);
  }

  // Expose public functions and variables
  return {
    init: init,
  };
})();

//
// Dashboard activity slideouts
//
app.dashboardActivitySlideout = (function () {
  let _body = null;
  let _openSlideout = null;
  let _clickedSlideout = null;
  let _loadingSlideout = false;
  let _isKeyboardEvent = false;

  function init() {
    const buttons = document.querySelectorAll('[data-activity-slideout]');
    if (buttons.length === 0) return;

    _body = document.querySelector('body');

    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        if (_loadingSlideout) return;

        _loadingSlideout = true;
        _clickedSlideout = button;
        _isKeyboardEvent = e.clientX === 0 && e.clientY === 0;
        loadSlideout(button.dataset.activitySlideout);

        // DATALAYER
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dashboardActivityClick',
          option: e.target.innerText.trim(),
        });
      });
    });
  }

  //
  // Load slideout
  //
  function loadSlideout(id) {
    fetch('/api/dashboard/getactivityhtml?id=' + id, {
      method: 'GET',
      headers: {
        'Content-Type': 'text/html',
      },
    })
      .then((response) => {
        // Check if the response is okay
        if (response.ok === false) throw new Error('Network response was not ok');

        return response.text();
      })
      .then((data) => {
        openSlideout(data);
        _loadingSlideout = false;
      })
      .catch((error) => {
        console.error(error);
        Toast(error, 'error');
        _loadingSlideout = false;
      });
  }

  //
  // Open slideout
  //
  function openSlideout(html) {
    _openSlideout = createSlideout(html);
    setTimeout(() => {
      _openSlideout.classList.add('is-open');

      if (_isKeyboardEvent) {
        _openSlideout.querySelector('[data-slideout-close]').focus();
      }
    }, 100);
  }

  //
  // Create slideout
  //
  function createSlideout(html) {
    // create slideout
    const slideout = document.createElement('div');
    slideout.classList.add('dsh__slideout');
    slideout.dataset.slideout = 'true';

    // create close button
    const closeButton = document.createElement('a');
    closeButton.classList.add('dsh__slideout__close');
    closeButton.setAttribute('aria-label', 'Close slideout');
    closeButton.dataset.slideoutClose = 'true';
    closeButton.href = '#';
    closeButton.innerHTML =
      '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">' +
      '<path fill="currentColor" d="M26.286 3.836c-0.482 0-0.963 0.183-1.329 0.549l-20.572 20.572c-0.733 0.733-0.733 1.925 0 2.659s1.925 0.733 2.659 0l20.572-20.572c0.733-0.733 0.733-1.925 0-2.659-0.367-0.367-0.848-0.549-1.329-0.549z"></path>' +
      '<path fill="currentColor" d="M5.714 3.833c-0.499 0-0.977 0.2-1.329 0.552-0.733 0.733-0.733 1.925 0 2.659l20.572 20.572c0.733 0.733 1.925 0.733 2.659 0s0.733-1.925 0-2.659l-20.572-20.572c-0.353-0.353-0.831-0.552-1.329-0.552z"></path>' +
      '</svg>';

    // add click event to close button
    closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      _isKeyboardEvent = e.clientX === 0 && e.clientY === 0;
      closeSlideout();
    });

    // create slideout inner and add html
    const slideoutInner = document.createElement('div');
    slideoutInner.classList.add('dsh__slideout__inner');
    slideoutInner.innerHTML = html;

    slideout.appendChild(closeButton);
    slideout.appendChild(slideoutInner);
    _body.appendChild(slideout);
    _body.classList.add('has-open-slideout');

    // init videos and lightboxes
    app.dashboardVideoLightbox.init();
    app.videos.init();

    return slideout;
  }

  //
  // Close slideout
  //
  function closeSlideout() {
    if (_openSlideout == null) return;

    _openSlideout.remove();
    _body.classList.remove('has-open-slideout');

    if (_clickedSlideout != null && _isKeyboardEvent) {
      _clickedSlideout.focus();
    }

    _openSlideout = null;
    _clickedSlideout = null;
    _isKeyboardEvent = false;
  }

  // Expose public functions and variables
  return {
    init: init,
  };
})();

//
// Dashboard video lightbox
//
app.dashboardVideoLightbox = (function () {
  function init() {
    const videos = document.querySelectorAll('[data-video-lightbox]:not([data-video-lightbox-initialized])');
    if (videos.length === 0) return;

    VideoLightbox(videos);
  }

  // Expose public functions and variables
  return {
    init: init,
  };
})();
